/**
 * Beyond the eastern parapet (lifted from lantern-water's
 * `eastern-garden.js`): three garden terraces become the walled back plots
 * of a lane of houses standing on the upper terraces. Tier tops share the
 * pixel meadow (and its draw) with the river banks; the paved lane, its stair,
 * the houses and a taller rank behind, lane shade, walls, gates, an orchard
 * corner, kitchen plots, washing, a shed, a well and a flower border, cold
 * frames, beehives, a cart and trellised vines. Nothing here is walkable. A
 * world-coordinate set piece: placed at the origin. Its own seed (90213; the
 * flower border its own, 9104). No params.
 */
import * as THREE from 'three';
import { CELLS as C, atlasMesh, backdropMaterial, type V3 } from '../backdrop-art.ts';
import { meadowMaterial, meadowPatch } from '../backdrop-ground.ts';
import { BAY, type RoofKind, type Style, TINTS, createBackdropBuilder } from '../backdrop-kit.ts';
import { createBackdropProps } from '../backdrop-props.ts';
import { floweringPlant } from '../botanical.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'eastern_garden',
  buildPrefab(ctx) {
    const p = new THREE.Group();
    p.name = 'eastern-terraced-garden';
    const { m, box } = ctx;
    const material = backdropMaterial(ctx.kit, m.windowCool);
    const b = createBackdropBuilder(ctx, 90213);
    const P = createBackdropProps(ctx, b);
    const { kit } = b;
    const roofOf = (rm: 'roof' | 'slate'): THREE.Material => (rm === 'slate' ? m.roofDark : m.roof);
    const meadow = meadowMaterial(ctx.kit);
    const tiers = [
      { x: 25.7, z: -7, width: 7.4, depth: 38, top: 0.28 },
      { x: 32.4, z: -9, width: 6, depth: 34, top: 0.92 },
      { x: 38.4, z: -10.5, width: 6, depth: 29, top: 1.58 },
    ];
    for (const { x, z, width, depth, top } of tiers) {
      const bottom = -0.38;
      box(p, x, (top - 0.05 + bottom) / 2, z, width, top - 0.05 - bottom, depth, m.stone);
      meadowPatch(p, meadow, x - width / 2, x + width / 2, z - depth / 2, z + depth / 2, top);
      box(p, x - width / 2 + 0.06, top + 0.035, z, 0.22, 0.1, depth + 0.1, m.stone);
      for (const side of [-1, 1]) box(p, x, top + 0.035, z + (side * depth) / 2, width, 0.1, 0.22, m.stone);
    }
    // The paved lane along the parapet, the cross-path and its stair.
    for (const [x, z, width, depth, top] of [
      [23.15, -7, 1.45, 37, 0.28],
      [26.6375, -0.4, 5.525, 1.65, 0.28],
    ] as const)
      box(p, x, top + 0.018, z, width, 0.035, depth, m.paving);
    {
      const edge = 29.4;
      const z = -0.4;
      const lower = 0.28;
      const upper = 0.92;
      const count = 4;
      const run = 1.6;
      for (let i = 0; i < count; i += 1) {
        const top = lower + ((upper - lower) * (i + 1)) / count;
        const x = edge - run + ((i + 0.5) * run) / count;
        box(p, x, (lower + top - 0.045) / 2, z, run / count + 0.005, top - lower - 0.045, 1.8, m.stone);
        box(p, x, top - 0.0225, z, run / count + 0.04, 0.045, 1.88, m.cream);
      }
    }

    // Houses on the upper terraces; their backs face the gardens (west).
    const T2 = 0.92;
    const T3 = 1.58;
    const row: readonly (readonly [z0: number, z1: number, floors: number, style: Style, tint: V3, roof: RoofKind, rm: 'roof' | 'slate'])[] = [
      [-25.6, -21.85, 3, 'plaster', TINTS.ochre, 'gableX', 'roof'],
      [-21.85, -18.1, 2, 'timber', TINTS.straw, 'gableZ', 'slate'],
      [-17.0, -13.25, 3, 'brick', TINTS.warm, 'gableX', 'roof'],
      [-13.25, -10.75, 4, 'plaster', TINTS.teal, 'gableZ', 'slate'],
      [-9.6, -4.6, 3, 'timber', TINTS.rust, 'gableX', 'roof'],
      [0.6, 4.35, 3, 'plaster', TINTS.amber, 'gableX', 'slate'],
      [4.35, 6.85, 2, 'plaster', TINTS.sage, 'gableZ', 'roof'],
      [7.9, 11.65, 3, 'timber', TINTS.straw, 'gableX', 'roof'],
    ];
    for (const [z0, z1, floors, style, tint, roof, rm] of row) {
      b.house(p, { x: 30.4 + 1.5 * BAY, z: (z0 + z1) / 2, bays: 3, deep: (z1 - z0) / BAY, floors, base: T2, style, tint, roof, roofMat: roofOf(rm), face: 'west', side: 'south', passage: z1 - z0 > 3, lit: 0.15 });
    }
    // A taller rank behind: only upper storeys and roofs clear the first row.
    for (const [z, deep, floors, tint, roof, rm] of [
      [-21, 4, 4, TINTS.sage, 'gableX', 'slate'],
      [-12.5, 3, 5, TINTS.straw, 'gableZ', 'roof'],
      [-3.5, 4, 4, TINTS.rust, 'gableX', 'roof'],
      [6.0, 3, 4, TINTS.teal, 'gableZ', 'slate'],
    ] as const)
      b.house(p, { x: 38.6, z, bays: 3, deep, floors, base: T3, style: 'plaster', tint, roof, roofMat: roofOf(rm), face: 'west', side: 'south', cheap: true, dormers: false, door: false });
    // Lanes between the houses turn out of sight.
    for (const [z0, z1] of [
      [-18.1, -17.0],
      [-4.6, 0.6],
      [6.85, 7.9],
    ] as const)
      kit.quad([34.9, T2, z1], [34.9, T2, z0], [34.9, T2 + 5, z0], [34.9, T2 + 5, z1], C.shade, [1, 1, 1], [0, 0, 1, 1], [0.7, 1]);

    // Back plots on the lowest terrace (x 24 … 29.3).
    const Y = 0.28;
    for (const z of [-20, -13, -6.2, 0.6, 6.4]) P.lowWall(p, 24.1, z, 29.2, z, Y, 0.5);
    for (const [z0, z1] of [
      [-25.8, -21.2],
      [-18.6, -8.2],
      [-6.2, -1.3],
      [0.6, 4.2],
      [5.8, 11.8],
    ] as const)
      P.lowWall(p, 24.1, z0, 24.1, z1, Y, 0.5);
    P.gate(p, 24.1, Y, -20.6, 1.0, 'z');
    P.gate(p, 24.1, Y, -7.2, 1.0, 'z');
    P.gate(p, 24.1, Y, 5.0, 1.0, 'z');
    // Plot 1: an old orchard corner, a woodpile against the terrace wall.
    P.tree(p, 26.2, Y, -24.0, 0.8);
    P.tree(p, 27.9, Y, -22.1, 0.72, true);
    P.woodpile(p, 28.8, Y, -24.4, 1.8, 'east');
    P.barrel(p, 25.0, Y, -21.0);
    // Plot 2: a kitchen garden in rows with bean canes.
    P.vegPlot(p, 26.0, Y, -16.6, 2.4, 4.8);
    P.vegPlot(p, 28.3, Y, -16.6, 1.2, 4.8, C.cabbage);
    // Plot 3: washing lines and a shed.
    P.laundry(p, 24.9, -11.8, 28.6, -11.8, Y, 5);
    P.laundry(p, 24.9, -8.9, 28.6, -8.9, Y, 4);
    P.shed(p, 28.4, Y, -7.3, 1.5, 1.2, 'south');
    // Plot 4: a well and a flower border.
    P.well(p, 26.5, Y, -3.9);
    const flowers = floweringPlant(p, ctx.kit.botanical, 3.2, 10, 9104);
    flowers.position.set(26.4, Y + 0.02, -1.9);
    // Plot 5: cold frames and cabbages.
    P.vegPlot(p, 26.4, Y, 2.5, 3.6, 2.8, C.cabbage);
    for (const z of [1.6, 3.4]) kit.box(28.7, Y + 0.18, z, 0.9, 0.36, 1.2, { top: C.winPlain, front: C.planks, east: C.planks, west: C.planks, back: null }, [0.9, 0.95, 0.95]);
    // Plot 6: beehives under an apple tree, a cart.
    P.tree(p, 27.4, Y, 9.2, 0.95);
    for (const z of [7.4, 8.3, 10.6]) {
      kit.box(25.4, Y + 0.3, z, 0.55, 0.55, 0.55, { top: null, front: C.planks, east: C.planks, west: C.planks, back: null }, [1, 0.95, 0.85]);
      box(p, 25.4, Y + 0.63, z, 0.7, 0.07, 0.7, m.roofDark);
    }
    P.cart(p, 28.2, Y, 11.0, Math.PI / 2);
    // Trellised vines on the terrace wall above plots 2 and 3.
    for (const z of [-16, -11]) {
      for (const dz of [-1.1, 1.1]) box(p, 29.25, 0.92 + 0.67, z + dz, 0.07, 1.34, 0.07, m.darkWood);
      for (const y of [0.45, 0.9, 1.28]) box(p, 29.25, 0.92 + y, z, 0.055, 0.045, 2.3, m.wood);
      P.bush(p, 29.1, 0.92 + 0.3, z, 1.2);
    }
    // Tier-3 odds and ends visible between the houses.
    P.tree(p, 36.0, T3, -1.9, 1.05);
    P.laundry(p, 35.9, 7.0, 35.9, 7.8, T2, 1);

    atlasMesh(p, kit, material, 'backdrop-east-atlas');
    const root = new THREE.Group();
    root.add(p);
    return { root };
  },
};
